/**
 * Listado de promociones (parrilla / listado tipo Propiedades).
 * Alta: /promociones/nueva · Edición: /promociones/:ref
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import FichaResumenPanel from "../components/propiedades/FichaResumenPanel";
import { mapearUnidadPromocionAFichaResumen } from "../components/propiedades/mapearUnidadPromocionAFichaResumen";

const VIEW_KEY = "promociones_vista_v1";

/** Anchos alineados con Propiedades.jsx. Solo `direccion` crece al maximizar. */
const COLUMNAS = [
  { id: "select", label: "", width: 40, sortable: false, fija: true },
  { id: "publicada", label: "Web", width: 50, sortable: false, fija: true },
  { id: "imagenes", label: "Imágenes", width: 160, sortable: false, fija: true },
  { id: "operaciones", label: "Op. activas", width: 150, sortable: true, fija: true },
  { id: "tipo", label: "Tipo", width: 120, sortable: true, fija: true },
  { id: "localidad", label: "Localidad", width: 100, sortable: true, fija: true },
  { id: "nombre", label: "Nombre promoción", width: 200, sortable: true, fija: true },
  { id: "agente", label: "Agente", width: 150, sortable: true, fija: true },
  { id: "direccion", label: "Dirección", width: 200, sortable: true, flex: true },
  { id: "fecha_alta", label: "Fecha de alta", width: 110, sortable: true, fija: true },
];

const THUMB_W = 160;
const THUMB_H = 90;

function formatFecha(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function thumbSrc(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return url.startsWith("/") ? url : `/${url}`;
}

/** URL usable en <img> (same-origin / absoluto). */
function fotoUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

function fotosSesion(s) {
  const list = Array.isArray(s?.fotos) ? s.fotos : [];
  const urls = list.map(fotoUrl).filter(Boolean);
  if (urls.length) return urls;
  const one = fotoUrl(s?.thumb_url);
  return one ? [one] : [];
}

function sortValue(s, colId) {
  switch (colId) {
    case "operaciones":
      return String(s.ref || "").toLowerCase();
    case "tipo":
      return String(s.tipo || "").toLowerCase();
    case "localidad":
      return String(s.localidad || s.ubicacion || "").toLowerCase();
    case "nombre":
      return String(s.nombre || "").toLowerCase();
    case "agente":
      return String(s.agente || "").toLowerCase();
    case "direccion":
      return String(s.direccion || "").toLowerCase();
    case "fecha_alta":
      return s.fecha_alta ? Date.parse(s.fecha_alta) || 0 : 0;
    default:
      return "";
  }
}

function IconoVistaPrevia({ color }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}

export default function Promociones() {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const navigate = useNavigate();
  const [sesiones, setSesiones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [sortCol, setSortCol] = useState("fecha_alta");
  const [sortDir, setSortDir] = useState("desc");
  const [ficha, setFicha] = useState({ abierta: false, pack: null, cargando: false });
  const [visor, setVisor] = useState({ abierto: false, fotos: [], indice: 0 });
  const [vista, setVista] = useState(() => {
    try {
      const v = localStorage.getItem(VIEW_KEY);
      return v === "lista" ? "lista" : "parrilla";
    } catch {
      return "parrilla";
    }
  });

  const abrirVisor = (fotos) => {
    const list = Array.isArray(fotos) ? fotos.filter(Boolean) : [];
    if (!list.length) return;
    setVisor({ abierto: true, fotos: list, indice: 0 });
  };

  const cerrarVisor = () => {
    setVisor({ abierto: false, fotos: [], indice: 0 });
  };

  const visorAnterior = () => {
    setVisor((v) => ({ ...v, indice: v.indice > 0 ? v.indice - 1 : v.fotos.length - 1 }));
  };

  const visorSiguiente = () => {
    setVisor((v) => ({ ...v, indice: v.indice < v.fotos.length - 1 ? v.indice + 1 : 0 }));
  };

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api
      .get("/movil/promocion/sesiones")
      .then((res) => {
        const list = Array.isArray(res.data?.sesiones) ? res.data.sesiones : [];
        setSesiones(list);
        setSeleccionadas((prev) => prev.filter((ref) => list.some((s) => s.ref === ref)));
      })
      .catch((err) => {
        console.error("[promociones] listar:", err);
        setError(err.response?.data?.error || err.message || "No se pudo cargar el listado");
        setSesiones([]);
      })
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiarVista = (v) => {
    setVista(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* */
    }
  };

  const filtradas = useMemo(() => {
    const list = [...sesiones];
    const col = COLUMNAS.find((x) => x.id === sortCol);
    if (!col || !col.sortable) return list;
    list.sort((a, b) => {
      const va = sortValue(a, sortCol);
      const vb = sortValue(b, sortCol);
      let cmp = 0;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), "es", { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [sesiones, sortCol, sortDir]);

  const toggleSort = (colId) => {
    const col = COLUMNAS.find((x) => x.id === colId);
    if (!col?.sortable) return;
    if (sortCol === colId) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(colId);
      setSortDir(colId === "fecha_alta" ? "desc" : "asc");
    }
  };

  const toggleSeleccion = (ref) => {
    setSeleccionadas((prev) => (prev.includes(ref) ? prev.filter((x) => x !== ref) : [...prev, ref]));
  };

  const toggleTodas = () => {
    if (seleccionadas.length === filtradas.length) setSeleccionadas([]);
    else setSeleccionadas(filtradas.map((s) => s.ref));
  };

  const abrirVistaPrevia = async (s) => {
    setFicha({ abierta: true, pack: null, cargando: true });
    try {
      const res = await api.get(`/movil/promocion/sesion/${encodeURIComponent(s.ref)}`);
      const datos = res.data?.datos || res.data?.sesion?.datos || {};
      const meta = res.data?.meta || res.data?.sesion?.meta || {};
      const imagenes = Array.isArray(res.data?.imagenes) ? res.data.imagenes : [];
      const unidades = Array.isArray(datos.unidades) ? datos.unidades : [];
      const unidad = unidades[0] || { ref: s.ref, tipologia: s.tipo };
      const fotos = imagenes
        .map((im) => {
          const path = im.limpia ? im.url_limpia : im.url_orig;
          if (!path) return null;
          // Absoluta same-origin (Vercel rewrite) o http ya resuelto — la galería no debe prefijar /api otra vez
          const url = /^https?:\/\//i.test(path)
            ? path
            : `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
          return {
            url,
            label: im.archivo,
            url_limpia: im.url_limpia,
            url_orig: im.url_orig,
            nombre: im.archivo,
            limpia: im.limpia,
          };
        })
        .filter(Boolean);
      const pack = mapearUnidadPromocionAFichaResumen(unidad, {
        datos,
        form: {
          ref: s.ref,
          titulo: s.nombre || meta.titulo,
          tituloWeb: datos?.promocion?.web_titulo,
          agente: s.agente || meta.agente,
        },
        fotos,
        archivosNombres: meta.archivos_nombres,
      });
      // Vista previa a nivel promoción: mostrar ref de la promo, no de la unidad
      if (pack?.p) {
        pack.p.referencia = s.ref;
        pack.p.operacion = s.operacion || "Venta";
        pack.p.alias = s.nombre;
        pack.p.titulo = s.nombre || pack.p.titulo;
      }
      setFicha({ abierta: true, pack, cargando: false });
    } catch (err) {
      console.error("[promociones] vista previa:", err);
      setFicha({ abierta: false, pack: null, cargando: false });
      alert("No se pudo abrir la vista previa: " + (err.response?.data?.error || err.message || err));
    }
  };

  const cardStyle = {
    background: t.bgCard,
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    overflow: "hidden",
    cursor: "pointer",
    transition: "box-shadow .15s, transform .15s",
  };

  const renderCelda = (s, colId) => {
    const op = s.operacion || "Venta";
    switch (colId) {
      case "select":
        return (
          <input
            type="checkbox"
            checked={seleccionadas.includes(s.ref)}
            onChange={() => toggleSeleccion(s.ref)}
            className="rounded"
            onClick={(e) => e.stopPropagation()}
          />
        );
      case "publicada":
        return (
          <span className="text-sm text-center block" style={{ color: s.publicar_web ? t.accentGreen : t.textMuted }}>
            {s.publicar_web ? "✓" : "✗"}
          </span>
        );
      case "imagenes": {
        const src = thumbSrc(s.thumb_url);
        const fotos = fotosSesion(s);
        return (
          <div
            className="relative overflow-hidden"
            style={{ width: THUMB_W, height: THUMB_H, maxWidth: THUMB_W, maxHeight: THUMB_H }}
            onClick={(e) => e.stopPropagation()}
          >
            {src ? (
              <img
                src={src}
                alt=""
                className="object-cover block cursor-pointer"
                style={{ width: THUMB_W, height: THUMB_H }}
                loading="lazy"
                onClick={() => abrirVisor(fotos)}
              />
            ) : (
              <div
                className="flex items-center justify-center text-xs"
                style={{
                  width: THUMB_W,
                  height: THUMB_H,
                  color: t.textMuted,
                  background: isDark ? t.bgPanel : "#e2e8f0",
                }}
              >
                —
              </div>
            )}
            {s.num_fotos > 0 ? (
              <span className="absolute top-1 right-1 bg-black/60 text-white text-[10px] font-bold rounded px-1 py-0.5 leading-none pointer-events-none">
                {s.num_fotos}
              </span>
            ) : null}
          </div>
        );
      }
      case "operaciones":
        return (
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                abrirVistaPrevia(s);
              }}
              className="absolute -top-1 right-0 p-0.5"
              style={{ color: t.textMuted }}
              title="Ficha resumen"
            >
              <IconoVistaPrevia color="currentColor" />
            </button>
            <div className="flex flex-col text-xs leading-tight" style={{ color: t.textSecondary }}>
              <span>
                <span style={{ color: op === "Venta" ? t.accentGreen : t.accentRed }}>{op === "Venta" ? "✓" : "✗"}</span>
                {" "}Venta{" "}
                {op === "Venta" ? (
                  <span className="font-mono" style={{ color: isDark ? "#fbbf24" : "#2563eb" }}>
                    {s.ref}
                  </span>
                ) : null}
              </span>
              <span>
                <span style={{ color: op === "Alquiler" ? t.accentGreen : t.accentRed }}>{op === "Alquiler" ? "✓" : "✗"}</span>
                {" "}Alquiler{" "}
                {op === "Alquiler" ? (
                  <span className="font-mono" style={{ color: t.accentBlue }}>
                    {s.ref}
                  </span>
                ) : null}
              </span>
              <span>
                <span style={{ color: op === "Alquiler vacacional" ? t.accentGreen : t.accentRed }}>
                  {op === "Alquiler vacacional" ? "✓" : "✗"}
                </span>
                {" "}Vacacional
              </span>
            </div>
          </div>
        );
      case "tipo":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {s.tipo || "Promoción"}
          </span>
        );
      case "localidad":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {s.localidad || s.ubicacion || "—"}
          </span>
        );
      case "nombre":
        return (
          <div className="min-w-0 text-left font-semibold truncate" style={{ color: t.textPrimary }} title={s.nombre || ""}>
            {s.nombre || "Sin nombre"}
          </div>
        );
      case "agente":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {s.agente || "—"}
          </span>
        );
      case "direccion":
        return (
          <span className="block text-left truncate" style={{ color: t.textPrimary }} title={s.direccion || ""}>
            {s.direccion || "—"}
          </span>
        );
      case "fecha_alta":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {formatFecha(s.fecha_alta || s.updated_at)}
          </span>
        );
      default:
        return <span style={{ color: t.textMuted }}>—</span>;
    }
  };

  const tableMinWidth = COLUMNAS.reduce((acc, c) => acc + c.width, 0);

  const estiloCol = (col) => {
    if (col.flex) {
      // Absorbe el sobrante al maximizar; el resto queda fijo
      return { width: "auto", minWidth: col.width };
    }
    return { width: col.width, minWidth: col.width, maxWidth: col.width };
  };

  return (
    <div
      className="flex flex-col min-h-0"
      style={{
        height: "calc(100vh - 49px)",
        background: t.bgApp,
        color: t.textPrimary,
      }}
    >
      <header
        className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 shrink-0"
        style={{ borderBottom: `1px solid ${t.border}`, background: t.bgCard }}
      >
        <div>
          <h1 className="text-lg font-semibold m-0" style={{ color: t.textPrimary }}>
            Promociones
          </h1>
          <p className="text-xs m-0 mt-0.5" style={{ color: t.textMuted }}>
            {cargando
              ? "Cargando…"
              : `${sesiones.length} promoción${sesiones.length === 1 ? "" : "es"}${
                  seleccionadas.length ? ` · ${seleccionadas.length} seleccionada${seleccionadas.length === 1 ? "" : "s"}` : ""
                }`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex rounded-lg overflow-hidden text-sm"
            style={{ border: `1px solid ${t.border}` }}
          >
            <button
              type="button"
              onClick={() => cambiarVista("parrilla")}
              className="px-3 py-1.5 font-medium"
              style={{
                background: vista === "parrilla" ? t.accentBlue : "transparent",
                color: vista === "parrilla" ? "#fff" : t.textMuted,
                border: "none",
                cursor: "pointer",
              }}
            >
              Parrilla
            </button>
            <button
              type="button"
              onClick={() => cambiarVista("lista")}
              className="px-3 py-1.5 font-medium"
              style={{
                background: vista === "lista" ? t.accentBlue : "transparent",
                color: vista === "lista" ? "#fff" : t.textMuted,
                border: "none",
                cursor: "pointer",
              }}
            >
              Listado
            </button>
          </div>
          <button
            type="button"
            onClick={() => navigate("/promociones/nueva")}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: t.accentBlue, border: "none", cursor: "pointer" }}
          >
            Nueva promoción
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-auto p-5">
        <style>{`
          @media (max-width: 1100px) {
            .promo-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          }
          @media (max-width: 640px) {
            .promo-grid { grid-template-columns: minmax(0, 1fr) !important; }
          }
        `}</style>
        {cargando ? (
          <div className="text-center py-20" style={{ color: t.textMuted }}>
            Cargando promociones…
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="font-medium mb-3" style={{ color: t.accentRed }}>
              {error}
            </p>
            <button
              type="button"
              onClick={cargar}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ border: `1px solid ${t.border}`, background: t.bgCard, color: t.textPrimary, cursor: "pointer" }}
            >
              Reintentar
            </button>
          </div>
        ) : sesiones.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-semibold text-base mb-2" style={{ color: t.textPrimary }}>
              Aún no hay promociones
            </p>
            <p className="text-sm mb-5" style={{ color: t.textMuted }}>
              Crea la primera con documentación del promotor.
            </p>
            <button
              type="button"
              onClick={() => navigate("/promociones/nueva")}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: t.accentBlue, border: "none", cursor: "pointer" }}
            >
              Nueva promoción
            </button>
          </div>
        ) : vista === "parrilla" ? (
          <div
            className="grid gap-4 promo-grid"
            style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
          >
            {filtradas.map((s) => (
              <article
                key={s.ref}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/promociones/${encodeURIComponent(s.ref)}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/promociones/${encodeURIComponent(s.ref)}`);
                  }
                }}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(15,23,42,.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  className="relative w-full"
                  style={{ aspectRatio: "4 / 3", background: isDark ? t.bgPanel : "#e2e8f0" }}
                >
                  {thumbSrc(s.thumb_url) ? (
                    <img
                      src={thumbSrc(s.thumb_url)}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="absolute inset-0 flex items-center justify-center text-sm"
                      style={{ color: t.textMuted }}
                    >
                      Sin foto
                    </div>
                  )}
                  <div
                    className="absolute inset-x-0 bottom-0 p-3 pt-10"
                    style={{
                      background: "linear-gradient(transparent, rgba(15,23,42,.78))",
                      color: "#fff",
                    }}
                  >
                    <div className="font-semibold text-base leading-tight line-clamp-2">{s.nombre}</div>
                    <div className="text-xs opacity-90 mt-1">{s.ref}</div>
                  </div>
                </div>
                <div className="px-3 py-2.5 text-xs flex flex-wrap gap-x-3 gap-y-1" style={{ color: t.textMuted }}>
                  {s.localidad || s.ubicacion ? <span>{s.localidad || s.ubicacion}</span> : null}
                  <span>{s.n_unidades || 0} uds.</span>
                  {s.agente ? <span>{s.agente}</span> : null}
                  {s.fecha_alta || s.updated_at ? <span>{formatFecha(s.fecha_alta || s.updated_at)}</span> : null}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div
            className="w-full rounded-xl overflow-hidden"
            style={{
              border: `2px solid ${t.border}`,
              background: t.bgCard,
              opacity: cargando ? 0.6 : 1,
            }}
          >
            <div className="overflow-x-auto w-full">
              <table
                className="border-collapse"
                style={{ width: "100%", tableLayout: "fixed", minWidth: tableMinWidth }}
              >
                <colgroup>
                  {COLUMNAS.map((col) => (
                    <col key={col.id} style={estiloCol(col)} />
                  ))}
                </colgroup>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                    {COLUMNAS.map((col) => {
                      const isActive = sortCol === col.id;
                      return (
                        <th
                          key={col.id}
                          className="text-center px-3 py-2 text-xs font-bold uppercase select-none"
                          style={{
                            ...estiloCol(col),
                            background: isDark ? t.bgPanel : "#f1f5f9",
                            color: t.textMuted,
                            borderRight: `1px solid ${t.border}`,
                            cursor: col.sortable ? "pointer" : "default",
                          }}
                          onClick={() => col.sortable && toggleSort(col.id)}
                        >
                          {col.id === "select" ? (
                            <input
                              type="checkbox"
                              checked={seleccionadas.length === filtradas.length && filtradas.length > 0}
                              onChange={toggleTodas}
                              className="rounded"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                              {col.label}
                              {col.sortable ? (
                                <span
                                  style={{
                                    opacity: isActive ? 1 : 0.35,
                                    fontSize: 10,
                                    color: isActive ? t.accentBlue : t.textMuted,
                                  }}
                                >
                                  {isActive ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                                </span>
                              ) : null}
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((s) => (
                    <tr
                      key={s.ref}
                      className="cursor-pointer"
                      style={{ borderBottom: `1px solid ${t.border}`, background: t.bgCard }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDark ? t.bgCardHover : t.accentBlueLight;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = t.bgCard;
                      }}
                      onClick={() => navigate(`/promociones/${encodeURIComponent(s.ref)}`)}
                    >
                      {COLUMNAS.map((col) => (
                        <td
                          key={col.id}
                          className={`${col.id === "imagenes" ? "p-0" : "px-3 py-2"} align-middle last:border-r-0`}
                          style={{
                            ...estiloCol(col),
                            borderRight: `1px solid ${t.border}`,
                            ...(col.id === "imagenes"
                              ? { width: THUMB_W, minWidth: THUMB_W, maxWidth: THUMB_W, padding: 0, overflow: "hidden" }
                              : null),
                          }}
                          onClick={
                            col.id === "select" || col.id === "imagenes"
                              ? (e) => e.stopPropagation()
                              : undefined
                          }
                        >
                          {renderCelda(s, col.id)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {ficha.abierta ? (
        <FichaResumenPanel
          p={ficha.pack?.p}
          d={ficha.pack?.d}
          cargando={ficha.cargando}
          onClose={() => setFicha({ abierta: false, pack: null, cargando: false })}
        />
      ) : null}

      {visor.abierto ? (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
          <button
            type="button"
            onClick={cerrarVisor}
            className="absolute top-4 right-4 text-white text-4xl font-light hover:text-gray-300 z-10"
          >
            ✕
          </button>

          {visor.fotos.length > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                visorAnterior();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-5xl font-light hover:text-gray-300 z-10"
            >
              ‹
            </button>
          ) : null}

          <img
            src={visor.fotos[visor.indice]}
            alt=""
            className="max-w-[98vw] max-h-[96vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {visor.fotos.length > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                visorSiguiente();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-5xl font-light hover:text-gray-300 z-10"
            >
              ›
            </button>
          ) : null}

          <div className="absolute bottom-4 text-white text-sm">
            {visor.indice + 1} / {visor.fotos.length}
          </div>
        </div>
      ) : null}
    </div>
  );
}
